import {
  ElementPropertyType,
  ElementPropertyName,
  IFactoryExpressionProperty,
  IFactoryProperty,
  IFactorySelectProperty,
  IElementExpressionProperty,
  IElementSelectProperty,
} from '../../models/element-property.model';
import { Replace } from '@shared/models/type-helpers.model';

/**
 * SBEF property (of type select)
 */
export type SbefElementProperty = Replace<
  IElementSelectProperty,
  {
    name: ElementPropertyName.SbefCode;
  }
>;

/**
 * Lifetime property (of type expression)
 */
export type LifetimeElementProperty = Replace<
  IElementExpressionProperty,
  { name: ElementPropertyName.Lifetime }
>;

export type ContentCoverageElementProperty = Replace<
  IElementExpressionProperty,
  { name: ElementPropertyName.ContentCoverage }
>;

/**
 * Type need to create a lifetime property
 */
export type FactoryLifetimeProperty = Replace<
  IFactoryExpressionProperty,
  { name: ElementPropertyName.Lifetime }
>;

/**
 * Type need to create an sbef property
 */
export type FactorySbefProperty = Replace<
  IFactorySelectProperty,
  { name: ElementPropertyName.SbefCode }
>;

export type CommonElementCategoryProperty =
  SbefElementProperty | LifetimeElementProperty;

export type SystemElementCategoryProperty =
  CommonElementCategoryProperty | ContentCoverageElementProperty;

/**
 * Factory property to create the sbef property on elements
 */
export const sbefCodeProperty: FactorySbefProperty = Object.freeze({
  type: ElementPropertyType.Select,
  name: ElementPropertyName.SbefCode,
  /**
   * To not store all sbef codes for EVERY element we only store a reference to the options.
   */
  options: ElementPropertyName.SbefCode,
  inheritFallback: true,
});

export const SBEF_LIFETIME_EXPRESSION = `getSBEFLifetime(${ElementPropertyName.SbefCode})`;

/**
 * Factory property to create the lifetime property on elements
 */
export const lifetimeProperty: FactoryLifetimeProperty = Object.freeze({
  type: ElementPropertyType.Expression,
  name: ElementPropertyName.Lifetime,
  unit: 'year',
  inheritFallback: false,
  fallbackCount: SBEF_LIFETIME_EXPRESSION,
  min: 0,
});

/**
 * Content coverage (0–100%) for system elements.
 * Expresses how complete the element's recipe is; results are divided by coverage
 * to compensate for missing content.
 */
export const contentCoverageProperty: Readonly<IFactoryProperty> =
  Object.freeze({
    name: ElementPropertyName.ContentCoverage,
    type: ElementPropertyType.Expression,
    unit: '%',
    min: 0.01,
    max: 1,
    fallbackCount: 1,
    hidden: true,
  });

/**
 * Properties that should be added to all (at leastmost) categories
 */
export const commonElementCategoryProperties = Object.freeze([
  sbefCodeProperty,
  lifetimeProperty,
]);

export const commonElementCategoryPropertyNames =
  commonElementCategoryProperties.map((p) => p.name);

export type CommonElementCategoryPropertyName =
  CommonElementCategoryProperty['name'];

export const systemElementCategoryProperties = Object.freeze([
  ...commonElementCategoryProperties,
  contentCoverageProperty,
]);

export const systemElementCategoryPropertyNames =
  systemElementCategoryProperties.map((p) => p.name);

export type SystemElementCategoryPropertyName =
  SystemElementCategoryProperty['name'];
